import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { filter, map, startWith } from 'rxjs/operators';
import { PlatformSectionSpyService } from '../../services/platform-section-spy.service';
import { pathFromUrl } from '../../../utils/platform-route.util';
import {
  normalizeSidenavLink,
  PlatformSidenavSubItem,
} from './platform-sidenav-group.models';

let sidenavGroupSeq = 0;

@Component({
  selector: 'app-platform-sidenav-group',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, FontAwesomeModule, TranslocoPipe],
  templateUrl: './platform-sidenav-group.component.html',
  styleUrl: './platform-sidenav-group.component.scss',
})
export class PlatformSidenavGroupComponent {
  private readonly router = inject(Router);
  private readonly sectionSpy = inject(PlatformSectionSpyService);
  private readonly groupSeq = sidenavGroupSeq++;

  readonly icon = input.required<IconDefinition>();
  readonly labelKey = input.required<string>();
  readonly routerLink = input.required<string | readonly string[]>();
  readonly children = input<readonly PlatformSidenavSubItem[]>([]);
  readonly collapsed = input(false);

  readonly chevron = faChevronRight;
  readonly panelId = `sidenav-group-${this.groupSeq}`;

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  readonly parentPath = computed(() => normalizeSidenavLink(this.routerLink()));

  readonly isRouteActive = computed(() => {
    const path = pathFromUrl(this.currentUrl());
    const parentPath = this.parentPath();
    return path === parentPath || path.startsWith(`${parentPath}/`);
  });

  readonly activeFragment = computed(() => {
    if (!this.isRouteActive()) {
      return null;
    }
    return (
      this.sectionSpy.sectionFor(this.parentPath()) ??
      this.currentUrl().split('#')[1] ??
      null
    );
  });

  readonly isParentHighlighted = computed(
    () => this.isRouteActive() && !this.activeFragment()
  );

  readonly expanded = computed(
    () => this.isRouteActive() && this.children().length > 0 && !this.collapsed()
  );

  /**
   * Resolves the route commands for a child item.
   * @param child - Nested sidenav item
   * @returns Router commands
   */
  childLink(child: PlatformSidenavSubItem): string | readonly string[] {
    return child.routerLink ?? this.routerLink();
  }

  /**
   * Whether the child item matches the currently visible section.
   * @param child - Nested sidenav item
   * @returns True when the child is the active section
   */
  isChildActive(child: PlatformSidenavSubItem): boolean {
    const fragment = child.fragment;
    if (!fragment) {
      return false;
    }
    return this.activeFragment() === fragment;
  }

  /**
   * Navigates to a nested item including its fragment.
   * @param child - Nested sidenav item
   * @param event - Click event
   */
  onChildActivate(child: PlatformSidenavSubItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (child.fragment) {
      this.sectionSpy.setSection(this.parentPath(), child.fragment);
    }
    const link = this.childLink(child);
    const commands = typeof link === 'string' ? [link] : [...link];
    void this.router.navigate(commands, { fragment: child.fragment });
  }

  /**
   * Opens the parent route without a section hash.
   * @param event - Click event
   */
  onParentActivate(event: Event): void {
    event.preventDefault();
    const link = this.routerLink();
    const commands = typeof link === 'string' ? [link] : [...link];
    void this.router.navigate(commands, { fragment: '' }).then(() => {
      document.querySelector<HTMLElement>('.view-content')?.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    });
  }
}
